import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import Hls, { ErrorData, ManifestLoadedData } from 'hls.js';

const ENABLE_LOGGING = false;

type Props = BindingProps<{
  streamURL: string;
  enabled: boolean;
}>

export enum StreamStatus {
  None = 'None',
  Active = 'Active',
  Inactive = 'Inactive'
}

const POLL_INTERVAL = 4000;

export class MediaStreamMonitor
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'startTimer' | 'cancelTimer' | 'cancelRequest'>(this, {
      enabled: computed,
      streamURL: computed,
      isStreamActive: computed,
      isStreamInactive: computed,
      streamState: observable,
      handleEnabled: action,
      startTimer: action,
      cancelTimer: action,
      cancelRequest: action,
      start: action,
      cancel: action,
      reset: action,
      refresh: action,
      run: action,
    });

    // handle enabled 
    reaction(() => this.enabled, () => this.handleEnabled());
  }

  get enabled(): boolean {
    return this.resolvedProps.enabled;
  }

  // #region Props
  get streamURL(): string | null {
    return this.resolvedProps.streamURL ?? null;
  }
  // #endregion

  get isStreamActive(): boolean {
    return this.streamState === StreamStatus.Active;
  }

  get isStreamInactive(): boolean {
    return this.streamState === StreamStatus.Inactive;
  }

  streamState: StreamStatus = StreamStatus.None;
  private hls: Hls | null = null;

  private timerId: ReturnType<typeof setTimeout> | null = null;

  handleEnabled() {
    ENABLE_LOGGING && console.log('handle enabled', this.enabled);
    if (this.enabled)
      this.start();
    else
      this.reset();
  }

  private startTimer() {
    this.cancelTimer();

    if (!this.enabled) {
      ENABLE_LOGGING && console.warn('monitor disabled, cannot start timer');
      return;
    }

    ENABLE_LOGGING && console.log('start timer');
    this.timerId = setTimeout(() => {
      this.refresh();
    }, POLL_INTERVAL);
  }

  private cancelTimer() {
    const timerId = this.timerId;
    if (timerId) {
      clearTimeout(timerId);
      ENABLE_LOGGING && console.log('timer canceled');
      this.timerId = null;
    }
  }

  private cancelRequest() {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  start() {
    this.refresh();
  }

  cancel() {
    this.cancelTimer();
    this.cancelRequest();
  }

  reset() {
    this.cancel();
    this.streamState = StreamStatus.None;
  }

  refresh() {
    this.cancel();
    this.run(false);
  }

  public run(once = true) {
    ENABLE_LOGGING && console.log('monitor: run');

    if (!this.enabled) {
      ENABLE_LOGGING && console.warn('monitor disabled, cannot run');
      // this.startTimer();
      return;
    }

    if (!this.streamURL) {
      ENABLE_LOGGING && console.log('no stream -> inactive');
      if (!this.isStreamInactive)
        this.emit('inactive');
      this.streamState = StreamStatus.Inactive;
      this.startTimer();
      return;
    }
    ENABLE_LOGGING && console.log('check stream');
    this.hls = new Hls();

    this.hls.config.autoStartLoad = false;
    this.hls.loadSource(this.streamURL);

    this.hls.on(Hls.Events.ERROR, (
      _event: typeof Hls.Events.ERROR,
      data: ErrorData
    ) => {
      ENABLE_LOGGING && console.log('monitor: error', data);
      if (!this.isStreamInactive)
        this.emit('inactive');
      this.streamState = StreamStatus.Inactive;
      this.hls?.destroy();
      this.hls = null;
      !once && this.startTimer();
    });

    this.hls.on(Hls.Events.MANIFEST_LOADED, (
      _event: typeof Hls.Events.MANIFEST_LOADED,
      _data: ManifestLoadedData
    ) => {
      ENABLE_LOGGING && console.log('monitor: manifest loaded')
      if (!this.isStreamActive)
        this.emit('active');
      this.streamState = StreamStatus.Active;
      this.hls?.destroy();
      this.hls = null;
      !once && this.startTimer();
    });

  }
}
